// client/src/coms/views/canvas_parts/canvas_room_view_parts/floodFill.js
import {
  BUCKET_ALPHA_EMPTY_THRESHOLD,
  BUCKET_EDGE_BLEND_ALPHA_THRESHOLD,
  BUCKET_EDGE_BLEND_PASSES,
} from './constants';
import { hexToRgb } from './colorUtils';
import { clampOpacity } from './toolState';
import { selectionContainsPoint } from './selectionUtils';

export function getPixelIndex(x, y, width) {
  return (y * width + x) * 4;
}

export function readPixel(data, index) {
  return {
    r: data[index],
    g: data[index + 1],
    b: data[index + 2],
    a: data[index + 3],
  };
}

export function colorsAreEqual(a, b) {
  return a.r === b.r && a.g === b.g && a.b === b.b && a.a === b.a;
}

export function getColorDistance(pixel, target) {
  return Math.max(
    Math.abs(pixel.r - target.r),
    Math.abs(pixel.g - target.g),
    Math.abs(pixel.b - target.b),
  );
}

export function pixelMatchesTarget(pixel, target, tolerance) {
  if (target.a <= BUCKET_ALPHA_EMPTY_THRESHOLD) {
    return pixel.a <= BUCKET_ALPHA_EMPTY_THRESHOLD;
  }

  return (
    Math.abs(pixel.r - target.r) <= tolerance
    && Math.abs(pixel.g - target.g) <= tolerance
    && Math.abs(pixel.b - target.b) <= tolerance
    && Math.abs(pixel.a - target.a) <= tolerance
  );
}

export function pixelMatchesFillFringe(pixel, target, tolerance) {
  if (target.a <= BUCKET_ALPHA_EMPTY_THRESHOLD) {
    return pixel.a <= Math.max(BUCKET_EDGE_BLEND_ALPHA_THRESHOLD, BUCKET_ALPHA_EMPTY_THRESHOLD + tolerance);
  }

  return (
    getColorDistance(pixel, target) <= tolerance
    && Math.abs(pixel.a - target.a) <= Math.max(tolerance * 2, 64)
  );
}

export function blendPixelWithColor(pixel, color) {
  const srcA = Math.max(0, Math.min(1, color.a / 255));
  const dstA = Math.max(0, Math.min(1, pixel.a / 255));
  const outA = srcA + (dstA * (1 - srcA));

  if (outA <= 0) {
    return { r: 0, g: 0, b: 0, a: 0 };
  }

  return {
    r: Math.round(((color.r * srcA) + (pixel.r * dstA * (1 - srcA))) / outA),
    g: Math.round(((color.g * srcA) + (pixel.g * dstA * (1 - srcA))) / outA),
    b: Math.round(((color.b * srcA) + (pixel.b * dstA * (1 - srcA))) / outA),
    a: Math.round(outA * 255),
  };
}

export function writePixel(data, index, color) {
  data[index] = color.r;
  data[index + 1] = color.g;
  data[index + 2] = color.b;
  data[index + 3] = color.a;
}

function hasMaskNeighbour(mask, index, x, y, width, height, wanted) {
  return (
    (x > 0 && Boolean(mask[index - 1]) === wanted)
    || (x < width - 1 && Boolean(mask[index + 1]) === wanted)
    || (y > 0 && Boolean(mask[index - width]) === wanted)
    || (y < height - 1 && Boolean(mask[index + width]) === wanted)
  );
}

function resizeFillMask(mask, width, height, amount, selection) {
  let result = new Uint8Array(mask);
  const steps = Math.min(20, Math.abs(Math.round(Number(amount) || 0)));
  const expand = amount > 0;

  for (let step = 0; step < steps; step += 1) {
    const next = new Uint8Array(result);

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = y * width + x;
        if (selection && !selectionContainsPoint(selection, x, y)) {
          next[index] = 0;
          continue;
        }

        if (expand) {
          if (!result[index] && hasMaskNeighbour(result, index, x, y, width, height, true)) {
            next[index] = 1;
          }
        } else if (result[index] && hasMaskNeighbour(result, index, x, y, width, height, false)) {
          next[index] = 0;
        }
      }
    }

    result = next;
  }

  return result;
}

function addFillFringeMask(data, width, height, initialMask, target, tolerance, selection) {
  let result = new Uint8Array(initialMask);

  for (let pass = 0; pass < BUCKET_EDGE_BLEND_PASSES; pass += 1) {
    const next = new Uint8Array(result);
    let changed = false;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const maskIndex = y * width + x;
        if (result[maskIndex]) continue;
        if (selection && !selectionContainsPoint(selection, x, y)) continue;
        if (!hasMaskNeighbour(result, maskIndex, x, y, width, height, true)) continue;

        const pixel = readPixel(data, getPixelIndex(x, y, width));
        if (!pixelMatchesFillFringe(pixel, target, tolerance)) continue;

        next[maskIndex] = 1;
        changed = true;
      }
    }

    result = next;
    if (!changed) break;
  }

  return result;
}

function getMaskBounds(mask, width, height) {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!mask[y * width + x]) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (maxX < minX || maxY < minY) return null;

  return {
    x: Math.max(0, minX - 3),
    y: Math.max(0, minY - 3),
    width: Math.min(width, maxX + 4) - Math.max(0, minX - 3),
    height: Math.min(height, maxY + 4) - Math.max(0, minY - 3),
  };
}

export function flipPixelRows(pixels, width, height) {
  if (!(pixels instanceof Uint8Array) || width <= 0 || height <= 0) return null;
  const rowBytes = width * 4;
  const flipped = new Uint8Array(pixels.length);

  for (let y = 0; y < height; y += 1) {
    const sourceOffset = y * rowBytes;
    const targetOffset = (height - 1 - y) * rowBytes;
    flipped.set(pixels.subarray(sourceOffset, sourceOffset + rowBytes), targetOffset);
  }

  return flipped;
}

export function floodFillPixels(
  sourcePixels,
  width,
  height,
  point,
  fillHex,
  tolerance,
  selection = null,
  opacity = 1,
  expansion = 0,
) {
  if (!(sourcePixels instanceof Uint8Array) || sourcePixels.length !== width * height * 4) return false;

  const x = Math.max(0, Math.min(width - 1, Math.floor(point.x)));
  const y = Math.max(0, Math.min(height - 1, Math.floor(point.y)));
  if (selection && !selectionContainsPoint(selection, x, y)) return false;

  const pixels = new Uint8Array(sourcePixels);
  const target = readPixel(pixels, getPixelIndex(x, y, width));
  const fillRgb = hexToRgb(fillHex);
  const replacement = { ...fillRgb, a: Math.round(clampOpacity(opacity) * 255) };
  if (colorsAreEqual(target, replacement)) return false;

  const visited = new Uint8Array(width * height);
  let filledMask = new Uint8Array(width * height);
  const stack = [[x, y]];

  while (stack.length) {
    const current = stack.pop();
    if (!current) continue;
    const currentX = current[0];
    const currentY = current[1];
    if (currentX < 0 || currentX >= width || currentY < 0 || currentY >= height) continue;
    if (selection && !selectionContainsPoint(selection, currentX, currentY)) continue;

    const visitIndex = currentY * width + currentX;
    if (visited[visitIndex]) continue;
    visited[visitIndex] = 1;

    const pixel = readPixel(pixels, getPixelIndex(currentX, currentY, width));
    if (!pixelMatchesTarget(pixel, target, tolerance)) continue;

    filledMask[visitIndex] = 1;
    stack.push([currentX + 1, currentY]);
    stack.push([currentX - 1, currentY]);
    stack.push([currentX, currentY + 1]);
    stack.push([currentX, currentY - 1]);
  }

  if (!filledMask.some(Boolean)) return false;

  filledMask = addFillFringeMask(pixels, width, height, filledMask, target, tolerance, selection);
  filledMask = resizeFillMask(filledMask, width, height, expansion, selection);

  const bounds = getMaskBounds(filledMask, width, height);
  if (!bounds) return false;

  for (let index = 0; index < filledMask.length; index += 1) {
    if (!filledMask[index]) continue;
    const pixelIndex = index * 4;
    const original = readPixel(pixels, pixelIndex);
    writePixel(pixels, pixelIndex, blendPixelWithColor(original, replacement));
  }

  return { changed: true, pixels, bounds };
}

export function floodFillCanvas(canvasEl, point, fillHex, tolerance, selection = null, opacity = 1, expansion = 0) {
  const ctx = canvasEl && canvasEl.getContext ? canvasEl.getContext('2d', { willReadFrequently: true }) : null;
  if (!ctx || !canvasEl) return false;

  let imageData;
  try {
    imageData = ctx.getImageData(0, 0, canvasEl.width, canvasEl.height);
  } catch {
    return false;
  }

  const result = floodFillPixels(
    new Uint8Array(imageData.data),
    canvasEl.width,
    canvasEl.height,
    point,
    fillHex,
    tolerance,
    selection,
    opacity,
    expansion,
  );
  if (!result) return false;

  imageData.data.set(result.pixels);
  ctx.putImageData(imageData, 0, 0);
  return { changed: true, bounds: result.bounds };
}
